import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Classable, Entity, Shapeable } from '@shapeable/types';
import { breakpoints } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { Grid, GridProps } from '@shapeable/ui';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { LinkCard } from '../entities/link-card';

const cls = classNames('explorer-home-link-cards');

const ENTER_DURATION = 0.42;
const STAGGER = 0.11;
const ENTER_EASE: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];
const ENTER_Y = 8;

const CardShellStyles = breakpoints({
  base: css`
    display: flex;
    flex-grow: 1;
    min-width: 0;
    width: 100%;
  `,
});

const CardShell = styled(motion.div)`${CardShellStyles}`;

const Wrap = styled.div`
  width: 100%;
`;

export type ExplorerHomeLinkCardsProps = Classable &
  Pick<GridProps, 'spacing' | 'minColumns' | 'tabletAutoColumns' | 'desktopAutoColumns'> & {
    items: Entity[];
  };

export const ExplorerHomeLinkCards: Shapeable.FC<ExplorerHomeLinkCardsProps> = (props) => {
  const {
    className,
    items,
    spacing = 4,
    minColumns = 1,
    tabletAutoColumns = 4,
    desktopAutoColumns = 4,
  } = props;

  const prefersReducedMotion = useReducedMotion();
  const skipMotion = prefersReducedMotion === true;

  const wrapRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapRef, { once: true, amount: 0 });

  const showCards = skipMotion || isInView;

  return (
    <Wrap ref={wrapRef}>
      <Grid
        className={cls.name(className)}
        spacing={spacing}
        minColumns={minColumns}
        tabletAutoColumns={tabletAutoColumns}
        desktopAutoColumns={desktopAutoColumns}
        itemKeys={items.map((item) => item.id)}
        items={items.map((entity, index) => (
          <CardShell
            key={entity.id}
            initial={skipMotion ? false : { opacity: 0, y: ENTER_Y }}
            animate={showCards ? { opacity: 1, y: 0 } : { opacity: 0, y: ENTER_Y }}
            transition={
              skipMotion
                ? { duration: 0 }
                : {
                    duration: ENTER_DURATION,
                    delay: index * STAGGER,
                    ease: ENTER_EASE,
                  }
            }
          >
            <LinkCard entity={entity} animate={false} />
          </CardShell>
        ))}
      />
    </Wrap>
  );
};

ExplorerHomeLinkCards.cls = cls;
